import React, { createContext, useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useParams } from "react-router";
import { connect } from "react-redux";
import { makeStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import {
  Button,
  Autocomplete,
  TextField,
  Tabs,
  Tab,
  Box,
  Fab,
  Typography,
  useTheme,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import Applications from "./ApplicationPermissions";
import CreateNewPermissions from "./CreateNewPermissions";
import Content from "../../../components/Content";
import PermissionsComponent from "./PermissionsComponent";
import AddPermission from "./AddPermission";
import {
  findSearchGroups,
  findSecComponents,
  findSecGroupComponents,
} from "../SecFunction";
import {
  showAddSecRole,
  showEditSecRole,
} from "../../../reducers/DialogsReducer";
import { hideSpinner, showSpinner } from "../../../reducers/UiReducer";
import Dialog from "../../../components/Dialog";
import { getAccessPermission } from "../../../reducers/PermissionsReducer";

const useStyles = makeStyles((theme) => ({
  autoComplete: {
    maxWidth: 300,
    marginTop: "10px",
  },
  tabs: {
    width: "100%",
    "& button": {
      "& > span": {
        textTransform: "none",
      },
    },
  },
  settings: {
    height: "100%",
    overflowY: "auto",
  },
  actions: {
    textAlign: "right",
    marginRight: "10px",
  },
  msg: {
    marginTop: 5,
    color: "#ff1744",
  },
}));

export const MainPermissionContext = createContext();

function Permissions(props) {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const { dataUpdate } = props;
  const [allGroups, setAllGroups] = useState([]);
  const [group, setGroup] = useState(null);
  const [tabValue, setTabValue] = useState("modules");
  const [showAddPermission, setShowAddPermission] = useState(false);
  const addActive = tabValue !== "create" && tabValue !== "code";
  const [roleAgencyID, setRoleAgencyID] = useState("");
  const [allComponents, setAllComponents] = useState([]);
  const [allGroupComponents, setAllGroupComponents] = useState([]);
  const [refreshComponets, setRefreshComponent] = useState(false);
  const { role: selectedRole } = useParams();
  const { search, pathname } = useLocation();
  const queryParams = new URLSearchParams(search);
  const roleId = queryParams.get("roleId");
  const [loaded, setLoaded] = useState(false);
  const [openCopyConfirmation, setOpenCopyConfirmation] = useState(false);

  const securityRolePermission = getAccessPermission(
    "security",
    "Roles And Permissions",
    "any"
  );
  const canSecurityRolePermissionAdd = securityRolePermission?.Create;

  useEffect(() => {
    fetchData();
  }, [refreshComponets]);

  useEffect(() => {
    if (selectedRole && loaded) {
      const role = allGroups.find((item) => item.ptsGroupID == selectedRole);
      if (role) {
        handleRoleChange(null, role);
      }
    }
  }, [selectedRole, loaded]);

  useEffect(() => {
    if (!roleId || allGroups.length === 0 || !loaded) return;

    const matchedRole = allGroups.find(
      (item) => String(item.ptsGroupID) === String(roleId)
    );

    if (matchedRole) {
      handleRoleChange(null, matchedRole);
    }

    history.replace({
      pathname,
    });
  }, [roleId, allGroups, loaded]);

  useEffect(() => {
    if (
      dataUpdate &&
      (dataUpdate.type === "add-role" || dataUpdate.type === "edit-role")
    )
      fetchData();
  }, [dataUpdate]);

  const fetchData = async () => {
    try {
      setLoaded(false);
      props.showSpinner();
      // setAllComponents([]);
      // setAllGroupComponents([]);
      const result = await Promise.all([
        findSearchGroups(),
        findSecComponents(),
        findSecGroupComponents(),
      ]);

      const groups = result[0] || [];
      const components = result[1] || [];
      const groupComponents = result[2] || [];

      setAllGroups(groups);
      setAllComponents(components);
      setAllGroupComponents(groupComponents);

      if (group) {
        const updatedRole = groups.find(
          (item) => item.ptsGroupID === group.ptsGroupID
        );
        if (updatedRole) {
          handleRoleChange(null, updatedRole);
        } else {
          setGroup(null);
          setRoleAgencyID("");
        }
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoaded(true);
      props.hideSpinner();
    }
  };

  const addSecRole = () => {
    setOpenCopyConfirmation(false);
    props.showAddSecRole({ type: "copy-role", ptsGroupID: group.ptsGroupID });
  };

  const editSecRole = () => props.showEditSecRole(group.ptsGroupID);

  const handleRoleChange = (e, newValue) => {
    setRoleAgencyID(newValue?.AgencyID || "");
    setGroup(newValue);
  };

  const handleCloseCopyConfirmation = () => {
    setOpenCopyConfirmation(false);
  };

  const renderCopyRoleActions = () => {
    return (
      <>
        <Button
          color="primary"
          variant="contained"
          autoFocus
          onClick={addSecRole}
        >
          <SaveIcon /> Confirm
        </Button>
        <Button color="primary" onClick={handleCloseCopyConfirmation}>
          <CloseIcon /> Cancel
        </Button>
      </>
    );
  };

  const renderRoles = () => {
    return (
      <div
        style={{
          display: "flex",
          gap: "40px",
          width: "100%",
          alignItems: "center",
        }}
      >
        <Autocomplete
          // disablePortal
          options={allGroups}
          style={{ minWidth: "300px" }}
          className={classes.autoComplete}
          renderInput={(params) => (
            <TextField {...params} label="Roles" variant="outlined" />
          )}
          onChange={handleRoleChange}
          getOptionLabel={(option) => option.Label}
          renderOption={(props, option) => (
            <li {...props}>
              {option.Label} {option.Type ? ` - ${option.Type}` : ""}
            </li>
          )}
          getOptionSelected={(option, value) =>
            option.ptsGroupID === value.ptsGroupID
          }
          size="small"
          value={group}
        />
        {roleAgencyID && (
          <Typography
            variant="body1"
            sx={{
              mt: 2,
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.common.white
                  : "black",
            }}
          >
            Agency ID:{" "}
            <strong
              style={{
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.primary.light
                    : theme.palette.primary.main,
              }}
            >
              {roleAgencyID}
            </strong>
          </Typography>
        )}

        {group && canSecurityRolePermissionAdd && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => setOpenCopyConfirmation(true)}
            startIcon={<FileCopyIcon />}
            style={{ width: "130px", maxHeight: "30px" }}
          >
            Copy Role
          </Button>
        )}
      </div>
    );
  };

  const renderTabs = () => {
    const handleTabChange = (event, newValue) => setTabValue(newValue);
    return (
      <div className={classes.settings}>
        <Tabs
          variant="scrollable"
          value={tabValue}
          onChange={handleTabChange}
          className={classes.tabs}
        >
          <Tab label="Modules" value="modules" />
          <Tab label="Security" value="security" />
          <Tab label="Codes" value="codes" />
          <Tab label="Applicants" value="applicants" />
          <Tab label="Employees" value="employees" />
          <Tab label="Training" value="training" />
          <Tab label="Fleet" value="fleet" />
          <Tab label="Equipment" value="equipment" />
          {group && <Tab label="Create New" value="create" />}
        </Tabs>
        <MainPermissionContext.Provider
          value={{
            role: group,
            allComponents,
            allGroupComponents,
            refreshComponets,
            setRefreshComponent,
            fetchData,
          }}
        >
          <div role="tabpanel" className={classes.tabPanel}>
            <Box p={3}>
              {tabValue === "modules" && group && <Applications />}
              {tabValue === "codes" && group && (
                <PermissionsComponent type="Codes" title="Codes" />
              )}
              {tabValue === "security" && group && (
                <PermissionsComponent type="Security" title="Security" />
              )}
              {tabValue === "cad" && group && (
                <PermissionsComponent type="CAD" title="CAD" />
              )}
              {tabValue === "applicants" && group && (
                <PermissionsComponent type="Applicants" title="Applicants" />
              )}
              {tabValue === "employees" && group && (
                <PermissionsComponent type="Employees" title="Employees" />
              )}

              {tabValue === "training" && group && (
                <PermissionsComponent type="Training" title="Training" />
              )}

              {tabValue === "fleet" && group && (
                <PermissionsComponent type="Fleet" title="Fleet" />
              )}
              {tabValue === "equipment" && group && (
                <PermissionsComponent type="Equipment" title="Equipment" />
              )}
              {/* {tabValue === 'default' && <DefaultPermissions />} */}
              {tabValue === "create" && <CreateNewPermissions />}
            </Box>
          </div>
        </MainPermissionContext.Provider>
      </div>
    );
  };

  return (
    <>
      <Content>
        <div className="m-3">
          <Typography
            variant="h4"
            sx={{
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.common.white
                  : "black",
            }}
          >
            Roles
          </Typography>
          {renderRoles()}
          {!group && tabValue !== "default" && tabValue !== "create" && (
            <p className={classes.msg}>Select the role to see permissions</p>
          )}
          <Typography
            variant="h4"
            sx={{
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.common.white
                  : "black",
              marginTop: 2,
            }}
          >
            Permissions
          </Typography>
          <div className={classes.actions}>
            {canSecurityRolePermissionAdd && group && (
              <Tooltip title="Add New Permission">
                <span>
                  <Fab
                    size="small"
                    color="secondary"
                    onClick={() => setShowAddPermission(true)}
                    disabled={!addActive}
                  >
                    <AddIcon />
                  </Fab>
                </span>
              </Tooltip>
            )}
          </div>
          {renderTabs()}
        </div>
      </Content>
      {showAddPermission && (
        <AddPermission
          close={() => setShowAddPermission(false)}
          tabValue={tabValue}
          fetchData={fetchData}
        />
      )}
      {
        <Dialog
          open={openCopyConfirmation}
          onClose={handleCloseCopyConfirmation}
          title={"Copy And Create New Roles"}
          actions={renderCopyRoleActions()}
          toolbar
        >
          Do you want to copy this role and create new one?
        </Dialog>
      }
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    dataUpdate: state.dataUpdate,
  };
};

export default connect(mapStateToProps, {
  showAddSecRole,
  showSpinner,
  hideSpinner,
  showEditSecRole,
})(Permissions);
